"use client"

import { useEffect, useMemo, useRef, useState, type ReactNode } from "react"
import { clsx } from "clsx"
import { useInView } from "react-intersection-observer"

import type { GID } from "@unlikelystudio/commerce-connector"
import Slider, { useSliderState } from "@unlikelystudio/react-slider"

import type { Nullish, PropsWithClassName } from "~/@types/generics"
import { FillImage } from "~/components/ui/FillImage"
import { Image, type ImageProps } from "~/components/ui/Image"
import type { TImage } from "~/components/ui/Image/_data/serializer"
import type { ShopTheLook } from "~/components/ui/Panels/ShopTheLook/_data/serializer"
import type { TProductHeader } from "~/components/ui/ProductHeader/_data/serializer"
import type { TProduct } from "~/components/ui/ProductHeader/_data/types"
import { ShopTheLookCta } from "~/components/ui/ProductHeader/components/ProductDetails/components/ShopTheLookCta"
import DesktopNavigation from "~/components/ui/ProductHeader/components/ProductMedias/components/DesktopNavigation"
import ProductImagesZoom, {
  useProductImagesZoom,
  type ProductImagesZoomProps,
} from "~/components/ui/ProductHeader/components/ProductMedias/components/ProductImagesZoom"
import { getDiptycsIdsFromTemplate } from "~/components/ui/ProductHeader/components/ProductMedias/utils/ get-diptycs-ids-from-template"
import SliderNavigation from "~/components/ui/SliderNavigation"
import Video, { type TVideo } from "~/components/ui/Video"
import { Ratio } from "~/components/abstracts/Ratio"

import { breakpoints } from "~/styles/variables/breakpoints"
import { elements } from "~/styles/variables/elements"

import * as css from "./styles.css"

type ProductMediasProps = PropsWithClassName<{
  isGiftCard?: Nullish<boolean>
  navigationClassName?: string
  items: TProductHeader["serializedProduct"]["medias"]
  template?: keyof typeof css.template
  cardTagsSlot?: ReactNode
  shopTheLookIds: GID[]
  shopTheLookItems: ShopTheLook
  serializedProduct: Pick<TProduct, "handle" | "title">
}>

const IMAGE_SIZES = [{ breakpoint: breakpoints.lg, ratio: 6 / 15 }, { ratio: 1 }]

function ProductMedias({
  className,
  navigationClassName,
  items,
  template = "default",
  cardTagsSlot,
  shopTheLookItems,
  shopTheLookIds,
  isGiftCard,
  serializedProduct,
}: ProductMediasProps) {
  const desktopImagesRef = useRef<HTMLElement[]>([])
  const [visibleDesktopItem, setVisibleDesktop] = useState<number>(0)

  const scrollToMedia = (index: number) => {
    const offsetTop = desktopImagesRef.current[index]?.getBoundingClientRect().top ?? 0

    window.scrollTo({
      top: offsetTop - parseInt(elements.navigation.desktopHeight) + window?.scrollY,
      left: 0,
      behavior: "smooth",
    })
  }

  const [slideState, setSliderState] = useSliderState()

  const productImagesZoom = useProductImagesZoom()
  const imagesMedias = items.map((item) => (!isVideo(item) ? item : null)).filter(Boolean)
  const currentImage = items?.[0]
  const infiniteSlider = false

  const templateDiptycs = getDiptycsIdsFromTemplate(template)

  const activeDiptyc = templateDiptycs.find((diptyc) => diptyc.indexOf(visibleDesktopItem) !== -1)
  const activeItems = useMemo(() => activeDiptyc ?? [visibleDesktopItem], [visibleDesktopItem])
  const mediaInDiptyc = (key: number) => templateDiptycs.some((diptyc) => diptyc.indexOf(key) > -1)

  return (
    <>
      <DesktopNavigation
        className={clsx(navigationClassName, css.desktopNavigation)}
        items={items}
        activeItems={activeItems}
        onClick={scrollToMedia}
      />
      <div className={clsx(className, css.DesktopMedias, css.template[template])}>
        {cardTagsSlot && <div className={clsx(css.cardTags)}>{cardTagsSlot}</div>}
        {items.map((media, key) => (
          <div
            key={`desktop_image_${key}`}
            className={clsx(
              css.DesktopMediaWrapper,
              mediaInDiptyc(key) ? css.DiptycMedia : null,
              isVideo(media) ? css.videoContainer : null
            )}
            ref={(element) => (desktopImagesRef.current[key] = element as HTMLElement)}
          >
            <DesktopMedia
              index={key}
              productImagesZoom={productImagesZoom}
              media={media}
              onInView={() => setVisibleDesktop(key)}
            />
          </div>
        ))}
      </div>
      <div className={clsx(className, css.mobileMediasContainer)}>
        {cardTagsSlot && <div className={clsx(css.cardTags)}>{cardTagsSlot}</div>}
        <ShopTheLookCta
          serializedProduct={serializedProduct}
          shopTheLookItems={shopTheLookItems}
          shopTheLookIds={shopTheLookIds}
          isMobile
        />
        <Slider
          className={clsx(css.MobileMedias, isGiftCard && css.giftCardImageHeight)}
          containerClassName={clsx(css.MobileContainer)}
          setSliderState={setSliderState}
          infinite={infiniteSlider}
          snap
        >
          {items.map((media, key) =>
            isVideo(media) ? (
              <Video
                className={clsx(css.mobileItem)}
                playerClassName={clsx(css.mobileMedia)}
                key={`mobile_media_${key}`}
                {...media}
              />
            ) : (
              <FillImage
                key={`mobile_media_${key}`}
                className={clsx(css.mobileItem)}
                priority={key === 0}
                onClick={() => {
                  productImagesZoom?.setImageIndex?.(key)
                  productImagesZoom?.openZoom?.()
                }}
                imageClassName={clsx(css.mobileMedia)}
                sizesFromBreakpoints={IMAGE_SIZES}
                asPlaceholder
                {...media}
              />
            )
          )}
        </Slider>
        <SliderNavigation className={clsx(css.sliderNavigation)} infinite={infiniteSlider} {...slideState} />
      </div>
      {currentImage && (
        <ProductImagesZoom
          {...productImagesZoom}
          onNextClick={() =>
            productImagesZoom?.setImageIndex?.(
              productImagesZoom.imageIndex + 1 < imagesMedias.length - 1 ? productImagesZoom.imageIndex + 1 : 0
            )
          }
          onPreviousClick={() =>
            productImagesZoom?.setImageIndex?.(
              productImagesZoom.imageIndex - 1 >= 0 ? productImagesZoom.imageIndex - 1 : imagesMedias?.length - 1
            )
          }
          images={imagesMedias}
        />
      )}
    </>
  )
}

export function DesktopMedia({
  media,
  onInView,
  productImagesZoom,
  index,
}: {
  media: TImage | TVideo
  onInView?(): void
  index: number
  productImagesZoom: ProductImagesZoomProps
}) {
  const desktopImagesProps = {
    fill: false,
    asPlaceholder: true,
    sizesFromBreakpoints: IMAGE_SIZES,
  } satisfies Partial<ImageProps>

  const [inViewRef, inView] = useInView({ initialInView: false })

  useEffect(() => {
    if (inView) onInView?.()
  }, [inView])

  return (
    <>
      <div ref={inViewRef} className={clsx(css.Marker)} />
      {isVideo(media) ? (
        <Ratio
          styleContainer={{
            paddingBottom: `${(Number(media.sources[0]?.height ?? 1) / Number(media.sources[0]?.width ?? 0)) * 100}%`,
          }}
        >
          <Video className={clsx(css.video)} {...media} />
        </Ratio>
      ) : (
        <Image
          imageClassName={clsx(css.image)}
          onClick={() => {
            productImagesZoom?.setImageIndex?.(index)
            productImagesZoom?.openZoom?.()
          }}
          {...desktopImagesProps}
          {...media}
        />
      )}
    </>
  )
}

export { type ProductMediasProps }
export default ProductMedias

export function isVideo(item: TVideo | TImage): item is TVideo {
  return "poster" in item
}
